import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Settings,
  Activity,
  Mail,
  User,
  Lock,
  Check,
  CheckCircle,
  Edit,
  X,
  ChevronLeft,
  ChevronRight,
  Palette,
  MessageSquare,
  Bug,
  Download,
  Trash2,
} from "lucide-react";
import type {
  AccountEditMode,
  AccountFormData,
  AccountActivity,
} from "../types";
import {
  passwordRequirementItems,
  type PasswordRequirements,
} from "@/utils/passwordValidation";
import {
  activityIconMap,
  defaultActivityIcon,
} from "@/utils/activityIcons";
import {
  ACTIVITY_TIME_PERIODS,
  WEBSITE_ACTION_TYPES,
  type ActivityTimePeriod,
  type WebsiteActionType,
} from "@/types/activity.types";
import { AppearanceSettings } from "./AppearanceSettings";
import { MessagesSettings } from "./MessagesSettings";
import { TicketSettings } from "./TicketSettings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";

type AccountSettingsTab =
  | "settings"
  | "appearance"
  | "messages"
  | "tickets"
  | "activity";

const ACCOUNT_TAB_STORAGE_KEY = "account-settings-tab";
const ACCOUNT_TAB_EVENT = "account-settings-tab-change";

const resolveStoredTab = (value: string | null): AccountSettingsTab => {
  if (
    value === "settings" ||
    value === "appearance" ||
    value === "messages" ||
    value === "tickets" ||
    value === "activity"
  ) {
    return value;
  }
  return "settings";
};

/**
 * Provides tabs for credential, username, and password edits inside the account settings area.
 */
interface AccountSettingsPanelProps {
  navigate: (page: string) => void;
  editMode: AccountEditMode;
  setEditMode: (mode: AccountEditMode) => void;
  currentEmail: string;
  isEmailVerified: boolean;
  isEmailSaveDisabled: boolean;
  formData: AccountFormData;
  setFormData: Dispatch<SetStateAction<AccountFormData>>;
  onSaveEmail: () => void;
  onSaveUsername: () => void;
  onSavePassword: () => void;
  onDownloadMyData: () => void;
  isDataExporting: boolean;
  onDeleteMyAccount: (currentPassword: string) => Promise<void>;
  isAccountDeleting: boolean;
  passwordRequirements: PasswordRequirements;
  passwordsMatch: boolean;
  isPasswordSaveDisabled: boolean;
  recentActivity: AccountActivity[];
  activitySearch: string;
  onActivitySearchChange: (value: string) => void;
  activityActionType: WebsiteActionType | "ALL";
  onActivityActionTypeChange: (value: WebsiteActionType | "ALL") => void;
  activityTimePeriod: ActivityTimePeriod;
  onActivityTimePeriodChange: (value: ActivityTimePeriod) => void;
  activityPageSize: number;
  onActivityPageSizeChange: (value: number) => void;
  activityPage: number;
  activityTotalPages: number;
  activityTotalItems: number;
  onActivityPageChange: (page: number) => void;
  homeAiSearchEnabled: boolean;
  homeAiSearchForcedOff: boolean;
  homeAiSearchSaving: boolean;
  onHomeAiSearchToggle: (enabled: boolean) => void;
  t: (key: string) => string;
  language: string;
}

/**
 * Settings panel exposing email/username/password editors with localized copy and action buttons.
 */
export function AccountSettingsPanel({
  navigate,
  editMode,
  setEditMode,
  currentEmail,
  isEmailVerified,
  isEmailSaveDisabled,
  formData,
  setFormData,
  onSaveEmail,
  onSaveUsername,
  onSavePassword,
  onDownloadMyData,
  isDataExporting,
  onDeleteMyAccount,
  isAccountDeleting,
  passwordRequirements,
  passwordsMatch,
  isPasswordSaveDisabled,
  recentActivity,
  activitySearch,
  onActivitySearchChange,
  activityActionType,
  onActivityActionTypeChange,
  activityTimePeriod,
  onActivityTimePeriodChange,
  activityPageSize,
  onActivityPageSizeChange,
  activityPage,
  activityTotalPages,
  activityTotalItems,
  onActivityPageChange,
  homeAiSearchEnabled,
  homeAiSearchForcedOff,
  homeAiSearchSaving,
  onHomeAiSearchToggle,
  t,
  language,
}: AccountSettingsPanelProps) {
  const dateLocale = language === "de" ? "de-DE" : "en-US";
  const numberFormatter = new Intl.NumberFormat(dateLocale);
  const maxFieldLength = 255;
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const mutedTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const verifiedIconStyle = {
    color: "color-mix(in srgb, var(--primary) 70%, var(--foreground))",
  } as const;
  const unverifiedIconStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const requirementsPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--surface) 80%, var(--background))",
  } as const;
  const requirementMetStyle = {
    color: "color-mix(in srgb, var(--primary) 70%, var(--foreground))",
  } as const;
  const requirementUnmetStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const activityIconBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 16%, var(--background))",
    border: "1px solid color-mix(in srgb, var(--accent) 28%, var(--background))",
    color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
  } as const;
  const activityRowBorderStyle = {
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const aiSearchStatus = homeAiSearchForcedOff
    ? "forced-off"
    : homeAiSearchEnabled
      ? "on"
      : "off";
  const aiSearchStatusLabel =
    aiSearchStatus === "on"
      ? t("account.aiSearchStatusOn")
      : aiSearchStatus === "forced-off"
        ? t("account.aiSearchStatusForcedOff")
        : t("account.aiSearchStatusOff");
  const aiSearchStatusHint =
    aiSearchStatus === "on"
      ? t("account.aiSearchStatusHintOn")
      : aiSearchStatus === "forced-off"
        ? t("account.aiSearchStatusHintForcedOff")
        : t("account.aiSearchStatusHintOff");
  const aiSearchStatusBadgeClass =
    aiSearchStatus === "on"
      ? "bg-green-100 text-green-800 border-green-300"
      : aiSearchStatus === "forced-off"
        ? "bg-amber-100 text-amber-800 border-amber-300"
        : "bg-slate-200 text-slate-700 border-slate-300";
  const passwordFields: Array<[keyof AccountFormData, string]> = [
    ["currentPassword", "account.currentPassword"],
    ["newPassword", "account.newPassword"],
    ["confirmPassword", "account.confirmPassword"],
  ];
  const updateField = (field: keyof AccountFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>(() => {
    if (typeof window === "undefined") return "settings";
    return resolveStoredTab(sessionStorage.getItem(ACCOUNT_TAB_STORAGE_KEY));
  });
  const [maxPaginationButtons, setMaxPaginationButtons] = useState(() => {
    if (typeof window === "undefined") return 6;
    const width = window.innerWidth;
    if (width < 480) return 4;
    if (width < 1024) return 6;
    return 10;
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const compute = () => {
      const width = window.innerWidth;
      setMaxPaginationButtons(width < 480 ? 4 : width < 1024 ? 6 : 10);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleTabEvent = () => {
      setActiveTab(
        resolveStoredTab(sessionStorage.getItem(ACCOUNT_TAB_STORAGE_KEY))
      );
    };
    window.addEventListener(ACCOUNT_TAB_EVENT, handleTabEvent);
    return () => window.removeEventListener(ACCOUNT_TAB_EVENT, handleTabEvent);
  }, []);

  const formatActivitySummary = (
    start: number,
    end: number,
    count: number
  ) => {
    const template =
      t("account.activityResultsSummary") ||
      "Showing {{start}}-{{end}} of {{count}} results";
    return template
      .replace(/\{\{start\}\}/g, numberFormatter.format(start))
      .replace(/\{\{end\}\}/g, numberFormatter.format(end))
      .replace(/\{\{count\}\}/g, numberFormatter.format(count));
  };

  const activityRangeStart =
    activityTotalItems > 0 ? (activityPage - 1) * activityPageSize + 1 : 0;
  const activityRangeEnd =
    activityTotalItems > 0
      ? Math.min(activityPage * activityPageSize, activityTotalItems)
      : 0;

  const handleTabChange = (value: string) => {
    const next = resolveStoredTab(value);
    setActiveTab(next);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(ACCOUNT_TAB_STORAGE_KEY, next);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword.trim()) {
      setDeletePasswordError(t("account.accountDeletePasswordRequired"));
      return;
    }
    setDeletePasswordError(null);
    try {
      await onDeleteMyAccount(deletePassword);
      setDeletePassword("");
      setDeleteDialogOpen(false);
    } catch {
      // Error toast is shown by parent handler.
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 tabs-list-standard lg:flex lg:h-auto lg:flex-col lg:items-stretch lg:gap-2">
          <TabsTrigger
            value="settings"
            className="gap-2 tabs-trigger-standard lg:justify-start lg:flex-none lg:w-full"
            style={primaryTextStyle}
          >
            <Settings className="w-4 h-4" style={iconStyle} />
            {t("account.settings")}
          </TabsTrigger>
          <TabsTrigger
            value="appearance"
            className="gap-2 tabs-trigger-standard lg:justify-start lg:flex-none lg:w-full"
            style={primaryTextStyle}
          >
            <Palette className="w-4 h-4" style={iconStyle} />
            {t("appearance.title")}
          </TabsTrigger>
          <TabsTrigger
            value="messages"
            className="gap-2 tabs-trigger-standard lg:justify-start lg:flex-none lg:w-full"
            style={primaryTextStyle}
          >
            <MessageSquare className="w-4 h-4" style={iconStyle} />
            {t("account.messages")}
          </TabsTrigger>
          <TabsTrigger
            value="tickets"
            className="gap-2 tabs-trigger-standard lg:justify-start lg:flex-none lg:w-full"
            style={primaryTextStyle}
          >
            <Bug className="w-4 h-4" style={iconStyle} />
            {t("account.tickets")}
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="gap-2 tabs-trigger-standard lg:justify-start lg:flex-none lg:w-full"
            style={primaryTextStyle}
          >
            <Activity className="w-4 h-4" style={iconStyle} />
            {t("account.activity")}
          </TabsTrigger>
        </TabsList>

        <div className="min-w-0">
      <TabsContent value="settings" className="space-y-6 mt-6 lg:mt-0">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
              <Mail className="w-5 h-5" style={iconStyle} />
              {t("account.email")}
            </CardTitle>
            <CardDescription className="card-description-text" style={primaryTextStyle}>
              {t("account.changeEmailDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {editMode === "email" ? (
              <div className="space-y-4">
                <div className="space-y-2 max-w-xl">
                  <Label htmlFor="new-email" style={primaryTextStyle}>
                    {t("account.newEmail")}
                  </Label>
                  <Input
                    id="new-email"
                    type="email"
                    className="w-full max-w-xl"
                    maxLength={maxFieldLength}
                    value={formData.newEmail}
                    onChange={(e) => updateField("newEmail")(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={onSaveEmail}
                    className="btn-primary"
                    disabled={isEmailSaveDisabled}
                  >
                    {t("common.save")}
                  </Button>
                  <Button variant="outline" onClick={() => setEditMode("none")}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p
                    className="break-words [overflow-wrap:anywhere]"
                    style={primaryTextStyle}
                  >
                    {currentEmail}
                  </p>
                  <p
                    className="mt-1 flex items-center gap-1 break-words text-sm"
                    style={secondaryTextStyle}
                  >
                    <CheckCircle
                      className="w-3 h-3"
                      style={isEmailVerified ? verifiedIconStyle : unverifiedIconStyle}
                    />
                    {isEmailVerified
                      ? t("account.verified")
                      : t("account.notVerified")}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setEditMode("email")}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t("account.change")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
              <User className="w-5 h-5" style={iconStyle} />
              {t("account.username")}
            </CardTitle>
            <CardDescription className="card-description-text" style={primaryTextStyle}>
              {t("account.changeUsernameDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {editMode === "username" ? (
              <div className="space-y-4">
                <div className="space-y-2 max-w-xl">
                  <Label htmlFor="new-username" style={primaryTextStyle}>
                    {t("account.newUsername")}
                  </Label>
                  <Input
                    id="new-username"
                    type="text"
                    className="w-full max-w-xl"
                    maxLength={maxFieldLength}
                    value={formData.newUsername}
                    onChange={(e) => updateField("newUsername")(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={onSaveUsername} className="btn-primary">
                    {t("common.save")}
                  </Button>
                  <Button variant="outline" onClick={() => setEditMode("none")}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4">
                <p
                  className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]"
                  style={primaryTextStyle}
                >
                  {formData.newUsername}
                </p>
                <Button
                  variant="outline"
                  onClick={() => setEditMode("username")}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t("account.change")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
              <Lock className="w-5 h-5" style={iconStyle} />
              {t("account.password")}
            </CardTitle>
            <CardDescription className="card-description-text" style={primaryTextStyle}>
              {t("account.changePasswordDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {editMode === "password" ? (
              <div className="space-y-4">
                {passwordFields.map(([field, translation]) => (
                  <div className="space-y-2 max-w-xl" key={field}>
                    <Label htmlFor={field} style={primaryTextStyle}>
                      {t(translation)}
                    </Label>
                    <Input
                      id={field}
                      type="password"
                      className="w-full max-w-xl"
                      maxLength={maxFieldLength}
                      value={formData[field]}
                      onChange={(e) => updateField(field)(e.target.value)}
                    />
                  </div>
                ))}
                {formData.newPassword && (
                  <div className="p-3 rounded-lg space-y-2" style={requirementsPanelStyle}>
                    <p className="text-xs text-secondary mb-2">
                      {t("auth.passwordRequirementsTitle")}
                    </p>
                    <div className="space-y-1 text-sm">
                      {passwordRequirementItems.map(({ key, labelKey }) => {
                        const isMet = passwordRequirements[key];
                        return (
                          <div
                            key={key}
                            className="flex items-center gap-2"
                            style={isMet ? requirementMetStyle : requirementUnmetStyle}
                          >
                            {isMet ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            <span>{t(labelKey)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {formData.confirmPassword && !passwordsMatch && (
                  <p className="text-sm text-secondary flex items-center gap-1">
                    <X className="w-4 h-4" />
                    {t("auth.passwordsDoNotMatch")}
                  </p>
                )}
                {formData.confirmPassword && passwordsMatch && (
                  <p className="text-sm flex items-center gap-1" style={requirementMetStyle}>
                    <Check className="w-4 h-4" />
                    {t("auth.passwordsMatch")}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={onSavePassword}
                    className="btn-primary"
                    disabled={isPasswordSaveDisabled}
                  >
                    {t("common.save")}
                  </Button>
                  <Button variant="outline" onClick={() => setEditMode("none")}>
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p style={mutedTextStyle}>••••••••••••</p>
                <Button
                  variant="outline"
                  onClick={() => setEditMode("password")}
                  className="gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t("account.change")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
              <Download className="w-5 h-5" style={iconStyle} />
              {t("account.dataPrivacyTitle")}
            </CardTitle>
            <CardDescription className="card-description-text" style={primaryTextStyle}>
              {t("account.dataPrivacyDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-primary-heading">
                    {t("account.aiSearchToggleLabel")}
                  </p>
                  <p className="text-xs mt-1" style={secondaryTextStyle}>
                    {aiSearchStatusHint}
                  </p>
                </div>
                <Badge className={aiSearchStatusBadgeClass}>
                  {aiSearchStatusLabel}
                </Badge>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <label
                  htmlFor="account-home-ai-search-toggle"
                  className={
                    homeAiSearchForcedOff || homeAiSearchSaving
                      ? "cursor-not-allowed select-none opacity-70 text-sm"
                      : "cursor-pointer select-none text-sm"
                  }
                  style={secondaryTextStyle}
                >
                  {homeAiSearchEnabled
                    ? t("account.aiSearchTurnOff")
                    : t("account.aiSearchTurnOn")}
                </label>
                <Switch
                  id="account-home-ai-search-toggle"
                  checked={!homeAiSearchForcedOff && homeAiSearchEnabled}
                  onCheckedChange={(checked) =>
                    onHomeAiSearchToggle(Boolean(checked))
                  }
                  disabled={homeAiSearchForcedOff || homeAiSearchSaving}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={onDownloadMyData}
                disabled={isDataExporting || isAccountDeleting}
              >
                <Download className="w-4 h-4" />
                {isDataExporting
                  ? t("account.dataExportLoading")
                  : t("account.dataExportButton")}
              </Button>
            </div>
            <div className="rounded-md border p-4">
              <p className="text-sm mb-3" style={secondaryTextStyle}>
                {t("account.accountDeleteDescription")}
              </p>
              <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={(open) => {
                  setDeleteDialogOpen(open);
                  if (!open) {
                    setDeletePassword("");
                    setDeletePasswordError(null);
                  }
                }}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="gap-2"
                    disabled={isAccountDeleting || isDataExporting}
                  >
                    <Trash2 className="w-4 h-4" />
                    {t("account.accountDeleteButton")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("account.accountDeleteConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("account.accountDeleteConfirmDescription")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="delete-account-password" style={primaryTextStyle}>
                      {t("account.currentPassword")}
                    </Label>
                    <Input
                      id="delete-account-password"
                      type="password"
                      className="w-full max-w-xl"
                      maxLength={maxFieldLength}
                      value={deletePassword}
                      onChange={(event) => setDeletePassword(event.target.value)}
                      autoComplete="current-password"
                      disabled={isAccountDeleting}
                    />
                    {deletePasswordError ? (
                      <p className="text-sm text-destructive">{deletePasswordError}</p>
                    ) : null}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isAccountDeleting}>
                      {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(event) => {
                        event.preventDefault();
                        void handleDeleteConfirm();
                      }}
                      disabled={isAccountDeleting}
                      className="bg-destructive text-white hover:bg-destructive/90"
                    >
                      {isAccountDeleting
                        ? t("account.accountDeleteLoading")
                        : t("account.accountDeleteConfirmButton")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="appearance" className="space-y-6 mt-6 lg:mt-0">
        <AppearanceSettings />
      </TabsContent>

      <TabsContent value="messages" className="space-y-6 mt-6 lg:mt-0">
        <MessagesSettings navigate={navigate} />
      </TabsContent>

      <TabsContent value="tickets" className="space-y-6 mt-6 lg:mt-0">
        <TicketSettings navigate={navigate} />
      </TabsContent>

      <TabsContent value="activity" className="space-y-6 mt-6 lg:mt-0">
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-primary-heading font-semibold">
              {t("account.recentActivity")}
            </CardTitle>
            <CardDescription className="card-description-text" style={primaryTextStyle}>
              {t("account.recentActivityDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="activity-search" style={primaryTextStyle}>
                  {t("account.activitySearchLabel")}
                </Label>
                <Input
                  id="activity-search"
                  className="w-full max-w-xl"
                  maxLength={maxFieldLength}
                  value={activitySearch}
                  placeholder={t("account.activitySearchPlaceholder")}
                  onChange={(e) => onActivitySearchChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>{t("account.activityFilterLabel")}</Label>
                <Select
                  value={activityActionType}
                  onValueChange={(value) =>
                    onActivityActionTypeChange(value as WebsiteActionType | "ALL")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("account.activityFilterAll")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">
                      {t("account.activityFilterAll")}
                    </SelectItem>
                    {WEBSITE_ACTION_TYPES.map((action) => (
                      <SelectItem key={action} value={action}>
                        {t(`activity.${action}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {t("account.activityFilterTimeLabel")}
                </Label>
                <Select
                  value={activityTimePeriod}
                  onValueChange={(value) =>
                    onActivityTimePeriodChange(value as ActivityTimePeriod)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("account.activityFilterTime")} />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TIME_PERIODS.map((period) => (
                      <SelectItem key={period} value={period}>
                        {t(`account.activityFilterTime${period}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {t("account.activityResultsPerPage")}
                </Label>
                <Select
                  value={String(activityPageSize)}
                  onValueChange={(value) =>
                    onActivityPageSizeChange(Number(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="10" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-4 mt-6">
              {recentActivity.length === 0 ? (
                <p className="text-sm" style={secondaryTextStyle}>
                  {t("account.activityEmpty")}
                </p>
              ) : null}
              {recentActivity.map((activity) => {
                const Icon =
                  activityIconMap[activity.action] ?? defaultActivityIcon;
                return (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 pb-4 border-b last:border-0"
                    style={activityRowBorderStyle}
                  >
                    <div className="p-2 rounded-lg" style={activityIconBadgeStyle}>
                      <Icon className="w-4 h-4 icon-primary" style={iconStyle} />
                    </div>
                    <div className="flex-1">
                      <p style={primaryTextStyle}>
                        {t(`activity.${activity.action}`)}
                      </p>
                      <p className="text-sm text-secondary">{activity.title}</p>
                      <p className="text-xs mt-1" style={secondaryTextStyle}>
                        {new Date(activity.occurredAt).toLocaleDateString(
                          dateLocale
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {activityTotalPages > 1 && (
              <div className="flex justify-center items-center gap-2 w-full mt-6">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    onActivityPageChange(Math.max(1, activityPage - 1))
                  }
                  disabled={activityPage === 1}
                  aria-label={t("common.back")}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <div className="flex items-center gap-1 justify-center">
                  {renderPagination(
                    activityPage,
                    activityTotalPages,
                    onActivityPageChange,
                    maxPaginationButtons
                  )}
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    onActivityPageChange(
                      Math.min(activityTotalPages, activityPage + 1)
                    )
                  }
                  disabled={activityPage === activityTotalPages}
                  aria-label={t("common.next")}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
            {activityTotalItems > 0 && (
              <p className="text-xs mt-3 text-center" style={secondaryTextStyle}>
                {formatActivitySummary(
                  activityRangeStart,
                  activityRangeEnd,
                  activityTotalItems
                )}
              </p>
            )}
          </CardContent>
        </Card>
      </TabsContent>
        </div>
      </div>
    </Tabs>
  );
}

function renderPagination(
  currentPage: number,
  totalPages: number,
  setPage: (value: number) => void,
  maxVisible = 10
) {
  const pages: (number | "ellipsis")[] = [];

  const addPage = (page: number) => pages.push(page);
  const addEllipsis = () => {
    if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis");
    }
  };

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i += 1) addPage(i);
  } else {
    const windowSize = maxVisible - 2; // reserve spots for first & last
    let start = Math.max(2, currentPage - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;

    if (end >= totalPages) {
      end = totalPages - 1;
      start = end - windowSize + 1;
    }
    if (start <= 1) {
      start = 2;
      end = start + windowSize - 1;
    }
    if (end >= totalPages) {
      end = totalPages - 1;
    }

    addPage(1);
    if (start > 2) addEllipsis();

    for (let i = start; i <= end; i += 1) addPage(i);

    if (end < totalPages - 1) addEllipsis();
    addPage(totalPages);
  }

  return pages.map((page, idx) => {
    if (page === "ellipsis") {
      return (
        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-secondary">
          ...
        </span>
      );
    }

    return (
      <Button
        key={page}
        variant={currentPage === page ? "default" : "outline"}
        size="sm"
        onClick={() => setPage(page)}
      >
        {page}
      </Button>
    );
  });
}
